import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = '/api';
const BRAND = '#FF385C';

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const AdminStores = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null); // store object or null
  const [deleting, setDeleting] = useState(false);

  const fetchStores = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, limit: 20, search, sort });
      const res = await fetch(`${API_BASE}/admin/stores?${params}`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to load stores');
      const data = await res.json();
      setStores(data.stores);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, sort]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchStores(1), 300);
    return () => clearTimeout(debounce);
  }, [fetchStores]);

  const handleToggleVisibility = async (store) => {
    const newVal = !(store.is_public !== false);
    try {
      const res = await fetch(`${API_BASE}/admin/stores/${store.id}`, {
        method: 'PATCH',
        headers: headers(),
        body: JSON.stringify({ is_public: newVal }),
      });
      if (!res.ok) throw new Error('Failed to update visibility');
      setStores((prev) => prev.map((s) => s.id === store.id ? { ...s, is_public: newVal } : s));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/stores/${deleteModal.id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Delete failed');
      }
      setDeleteModal(null);
      fetchStores(pagination.page);
    } catch (err) {
      setError(err.message);
      setDeleteModal(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>Stores</h2>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center',
      }}>
        <input
          type="text"
          placeholder="Search stores..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '1 1 250px', padding: '0.6rem 1rem', border: '1px solid #d1d5db',
            borderRadius: 8, fontSize: '0.9rem', outline: 'none',
          }}
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          style={{
            padding: '0.6rem 1rem', border: '1px solid #d1d5db',
            borderRadius: 8, fontSize: '0.9rem', background: '#fff',
          }}
        >
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="name_asc">Name A-Z</option>
          <option value="name_desc">Name Z-A</option>
          <option value="products_desc">Most products</option>
          <option value="products_asc">Fewest products</option>
        </select>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '0.75rem 1rem', color: '#991b1b', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner-border" role="status" style={{ color: BRAND }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : stores.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>No stores found</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                  {['Store', 'City', 'Owner', 'Products', 'Visible', 'Created', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => (
                  <tr key={store.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{store.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>/{store.url_name}</div>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#4b5563' }}>{store.city}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#4b5563' }}>{store.owner_email || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#4b5563' }}>{store.product_count}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <button
                        onClick={() => handleToggleVisibility(store)}
                        style={{
                          width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                          background: store.is_public !== false ? '#10b981' : '#d1d5db',
                          position: 'relative', transition: 'background 0.2s ease',
                        }}
                        title={store.is_public !== false ? 'Public — click to hide' : 'Hidden — click to make public'}
                      >
                        <div style={{
                          width: 16, height: 16, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 3,
                          left: store.is_public !== false ? 21 : 3,
                          transition: 'left 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }} />
                      </button>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {new Date(store.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => navigate(`/admin/stores/${store.id}`)}
                        style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, marginRight: 12 }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => window.open(`/${store.city.toLowerCase()}/${store.url_name}`, '_blank')}
                        style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, marginRight: 12 }}
                      >
                        Live
                      </button>
                      <button
                        onClick={() => setDeleteModal(store)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: '1.25rem' }}>
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => fetchStores(p)}
              style={{
                width: 36, height: 36, borderRadius: 8, border: '1px solid #d1d5db',
                background: p === pagination.page ? BRAND : '#fff',
                color: p === pagination.page ? '#fff' : '#374151',
                fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '2rem', maxWidth: 420, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>Delete Store?</h3>
            <p style={{ color: '#4b5563', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              This will permanently delete <strong>{deleteModal.name}</strong> and all its products. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteModal(null)}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: 8, border: '1px solid #d1d5db',
                  background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none',
                  background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 500,
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStores;
